import { useCallback, useEffect, useRef } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowUpRight, X } from "lucide-react";
import type { CaseStudy } from "@/lib/cases-data";
import { cases } from "@/lib/cases-data";

/**
 * Einheitliche Shop-Vorschau: dezenter Browserrahmen mit echter Domain,
 * darunter das Projektbild in festem Seitenverhältnis.
 */
export function CaseShot({
  study,
  priority = false,
  className = "",
  onClick,
}: {
  study: CaseStudy;
  priority?: boolean;
  className?: string;
  onClick?: () => void;
}) {
  const img = (
    <img
      src={study.image}
      alt={study.imageAlt}
      width={study.width}
      height={study.height}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      className="aspect-[16/10] w-full bg-muted/20 object-cover object-top"
    />
  );

  return (
    <figure
      className={`overflow-hidden rounded-lg border border-border bg-card ${className}`}
    >
      <div className="flex items-center gap-2 border-b border-border bg-muted/30 px-3 py-2">
        <span aria-hidden className="flex gap-1.5">
          <span className="h-2 w-2 rounded-full bg-muted-foreground/40" />
          <span className="h-2 w-2 rounded-full bg-muted-foreground/30" />
          <span className="h-2 w-2 rounded-full bg-muted-foreground/20" />
        </span>
        <span className="truncate rounded-full bg-background/70 px-2.5 py-0.5 font-mono text-[10px] tracking-wide text-muted-foreground">
          {study.domain}
        </span>
      </div>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className="block w-full cursor-zoom-in"
          aria-label={`${study.shop}: Projektbild vergrößert ansehen`}
        >
          {img}
        </button>
      ) : (
        img
      )}
    </figure>
  );
}

/** Kompakte Projektkarte: die gesamte Karte ist EIN Link auf das Projekt. */
export function CaseCard({ study }: { study: CaseStudy }) {
  return (
    <a
      href={`/cases#${study.slug}`}
      aria-label={`${study.shop}: Projekt ansehen`}
      className="shadow-lift group flex h-full flex-col overflow-hidden rounded-lg border border-border bg-card transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <CaseShot study={study} className="rounded-none border-0 border-b" />
      <div className="flex flex-1 flex-col p-5">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
          {study.industry}
        </p>
        <h3 className="mt-2 text-base font-semibold">{study.shop}</h3>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {study.summary}
        </p>
        <span className="mt-auto inline-flex items-center gap-1.5 pt-4 text-sm font-semibold text-primary">
          Projekt ansehen <ArrowUpRight className="h-4 w-4" aria-hidden />
        </span>
      </div>
    </a>
  );
}

/** Schlanke Referenzsektion für die Leistungsseiten. */
export function CaseStrip({
  slugs,
  title = "Aus echten Projekten",
  intro,
}: {
  slugs: string[];
  title?: string;
  intro?: string;
}) {
  const items = slugs
    .map((s) => cases.find((c) => c.slug === s))
    .filter((c): c is CaseStudy => Boolean(c));

  return (
    <section className="border-b border-border/60 py-14">
      <div className="mx-auto max-w-6xl px-5">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold md:text-3xl">{title}</h2>
            {intro && (
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                {intro}
              </p>
            )}
          </div>
          <Link
            to="/cases"
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary"
          >
            Alle Kundenprojekte <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
        <div
          className={`mt-8 grid gap-5 ${items.length > 2 ? "md:grid-cols-3" : "md:grid-cols-2"}`}
        >
          {items.map((c) => (
            <CaseCard key={c.slug} study={c} />
          ))}
        </div>
      </div>
    </section>
  );
}

/** Zugänglicher Dialog für das vollständige Originalbild. */
export function CaseLightbox({
  study,
  onClose,
}: {
  study: CaseStudy | null;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab" || !dialogRef.current) return;
      const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
        'button, a[href], [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!study) return;
    const opener = document.activeElement as HTMLElement | null;
    document.addEventListener("keydown", handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = prevOverflow;
      opener?.focus?.();
    };
  }, [study, handleKey]);

  if (!study) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${study.shop}: ${study.imageAlt}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/90 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-5xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="Ansicht schließen"
          className="absolute -top-3 -right-3 z-10 rounded-full border border-border bg-card p-2.5 text-muted-foreground transition-colors hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
        <img
          src={study.image}
          alt={study.imageAlt}
          width={study.width}
          height={study.height}
          className="max-h-[80vh] w-full rounded-lg border border-border bg-card object-contain"
        />
        <p className="mt-3 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
          {study.domain}
        </p>
      </div>
    </div>
  );
}
